/**
 * Program Landing Pages that render the Project-First PLP (projects, career
 * impact, reviews & testimonials). Slugs must match `catalog-data.json`.
 *
 * The list launched as the Top 20 by traffic and bookings and was later
 * extended with six more programs. Every program listed here needs an entry in
 * `data/projects.json`, `data/outcomes.json`, and `data/testimonials.json`.
 *
 * JSON and files under `public/images/program/plp-static/` are a temporary
 * static content pack until these assets and copy live in Sanity. A CMS
 * migration can delete that public folder, drop the JSON files, and swap URLs
 * to Sanity CDNs.
 */
export const PLP_STATIC_ASSET_PREFIX = '/images/program/plp-static';

/** Design token shared by every Figma module in this layout. No v2 theme equivalent. */
export const CARD_BORDER_COLOR = '#F2F2F2';

/**
 * Figma `body/xsm`. The theme's `xs` token is 10px, which reads too small
 * against the design and fails contrast at the caption grey.
 */
export const CAPTION_FONT_SIZE = '12px';

/**
 * How many reviews the carousel pulls on the client. The server payload only
 * holds one page, which runs out after a couple of arrow clicks.
 */
export const CAROUSEL_REVIEW_COUNT = 15;

/** The modal lays reviews out as a 4-across grid, three rows deep. */
export const MODAL_REVIEW_PAGE_SIZE = 12;

/**
 * Attribution mark required on every Trustpilot review card. Exported from the
 * Figma module and downscaled to 3x the 67x16 slot it renders into.
 */
export const TRUSTPILOT_LOGO = {
  src: `${PLP_STATIC_ASSET_PREFIX}/trustpilot-logo.png`,
  alt: 'Trustpilot',
  width: 67,
  height: 16,
} as const;

export const GRADUATE_OUTCOMES_PERCENT = 96;

/**
 * Salary figures are estimates, so this hedge has to ship alongside every
 * rendered salary tier.
 */
export const SALARY_DISCLAIMER = 'Salary estimates based on public market data. Individual results vary.';

export type ProjectFirstPlpProgram = {
  key: string;
  slug: string;
};

export const PROJECT_FIRST_PLP_PROGRAMS: ProjectFirstPlpProgram[] = [
  { key: 'nd900', slug: 'agentic-ai--nd900' },
  { key: 'nd089', slug: 'ai-programming-python-nanodegree--nd089' },
  { key: 'nd608', slug: 'generative-ai--nd608' },
  { key: 'nd770', slug: 'ai-powered-software-engineer--nd770' },
  { key: 'nd881', slug: 'ai-trading-strategies--nd881' },
  { key: 'nd002', slug: 'data-analyst-nanodegree--nd002' },
  { key: 'nd036', slug: 'product-manager-nanodegree--nd036' },
  { key: 'nd000', slug: 'intro-to-programming-nanodegree--nd000' },
  { key: 'nd898', slug: 'ai-artificial-intelligence-nanodegree--nd898' },
  { key: 'nd018', slug: 'digital-marketing-nanodegree--nd018' },
  { key: 'nd025', slug: 'data-scientist-nanodegree--nd025' },
  { key: 'nd101', slug: 'deep-learning--nd101' },
  { key: 'nd027', slug: 'data-engineering-with-aws--nd027' },
  { key: 'nd209', slug: 'robotics-software-engineer--nd209' },
  { key: 'nd104', slug: 'programming-for-data-science-nanodegree--nd104' },
  { key: 'nd213', slug: 'c-plus-plus-nanodegree--nd213' },
  { key: 'nd098', slug: 'business-analytics-nanodegree--nd098' },
  { key: 'nd189', slug: 'aws-machine-learning-engineer-nanodegree--nd189' },
  { key: 'nd256', slug: 'data-structures-and-algorithms-nanodegree--nd256' },
  { key: 'nd0821', slug: 'machine-learning-dev-ops-engineer-nanodegree--nd0821' },
  // Added after the original Top 20 ranking, so they are appended rather than sorted in.
  { key: 'nd7426', slug: 'ai-engineering-with-claude--nd7426' },
  { key: 'nd088', slug: 'ai-product-manager-nanodegree--nd088' },
  { key: 'nd229', slug: 'intro-to-machine-learning-nanodegree--nd229' },
  { key: 'nd901', slug: 'agentic-ai-engineer-with-langchain-and-langgraph--nd901' },
  { key: 'nd054', slug: 'ai-for-business-leaders--nd054' },
  { key: 'nd0013', slug: 'self-driving-car-engineer-nanodegree--nd0013' },
];

export const PROJECT_FIRST_PLP_PROGRAM_KEYS = PROJECT_FIRST_PLP_PROGRAMS.map((p) => p.key);

export const PROJECT_FIRST_PLP_PROGRAM_SLUGS = PROJECT_FIRST_PLP_PROGRAMS.map((p) => p.slug);

export function isProjectFirstPlpProgramKey(key: string | undefined | null): boolean {
  return Boolean(key && PROJECT_FIRST_PLP_PROGRAM_KEYS.includes(key));
}
